import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { ApiError, apiUrl, request, tenantStore, tokenStore } from '@/lib/api';
import i18n, { currentLocale } from '@/lib/i18n';

import {
  adminUsersSchema,
  auditSchema,
  businessStatusSchema,
  createdBusinessSchema,
  inviteSchema,
  reportSchema,
  type Granularity,
  type ReportKind,
  type Role,
} from './admin-schemas';

/*
  פעולות המפעיל, משתמשים, יומן ודוחות. כל קריאה וכל פעולה נרשמות בשרת (יומן
  הפלטפורמה + שורת לוג) — אין כאן רענון אוטומטי, ואחרי כל כתיבה מרעננים רק את
  מה שהשתנה.
*/

const ROOT = ['platform-admin'] as const;
const base = { staleTime: 60_000, refetchOnWindowFocus: false } as const;

export function useAdminUsers(search: string, enabled = true) {
  return useQuery({
    queryKey: [...ROOT, 'users', search],
    queryFn: ({ signal }) => {
      const qs = search ? `?${new URLSearchParams({ q: search }).toString()}` : '';
      return request(`/platform-admin/users${qs}`, { schema: adminUsersSchema, signal });
    },
    enabled,
    placeholderData: keepPreviousData,
    ...base,
  });
}

export function useBusinessUsers(id: string | null) {
  return useQuery({
    queryKey: [...ROOT, 'business-users', id],
    queryFn: ({ signal }) =>
      request(`/platform-admin/businesses/${encodeURIComponent(id!)}/users`, {
        schema: adminUsersSchema,
        signal,
      }),
    enabled: id !== null,
    ...base,
  });
}

export function useAuditLog(businessId?: string) {
  return useQuery({
    queryKey: [...ROOT, 'audit', businessId ?? null],
    queryFn: ({ signal }) => {
      const qs = businessId ? `?businessId=${encodeURIComponent(businessId)}` : '';
      return request(`/platform-admin/audit${qs}`, { schema: auditSchema, signal });
    },
    ...base,
  });
}

/** דף נוסף ביומן (ישן יותר). */
export function fetchAuditPage(before: string) {
  return request(`/platform-admin/audit?before=${encodeURIComponent(before)}`, {
    schema: auditSchema,
  });
}

export interface ReportFilter {
  from: string;
  to: string;
  granularity: Granularity;
  businessId?: string;
}

const reportQuery = (f: ReportFilter) => {
  const qs = new URLSearchParams({ from: f.from, to: f.to, granularity: f.granularity });
  if (f.businessId) qs.set('businessId', f.businessId);
  return qs;
};

export function useReport(filter: ReportFilter) {
  return useQuery({
    queryKey: [...ROOT, 'report', filter],
    queryFn: ({ signal }) =>
      request(`/platform-admin/reports?${reportQuery(filter).toString()}`, {
        schema: reportSchema,
        signal,
      }),
    placeholderData: keepPreviousData,
    ...base,
  });
}

/**
 * CSV מהשרת (UTF-8 עם BOM). קישור רגיל לא שולח את ה-Bearer, ולכן מביאים blob
 * ומורידים אותו מקומית.
 */
export async function downloadReportCsv(filter: ReportFilter, kind: ReportKind): Promise<void> {
  const qs = reportQuery(filter);
  qs.set('kind', kind);
  const path = `/platform-admin/reports/export?${qs.toString()}`;
  const headers: Record<string, string> = { 'Accept-Language': currentLocale() };
  const token = tokenStore.get();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const tenant = tenantStore.get() ?? import.meta.env.VITE_TENANT;
  if (tenant) headers['X-Tenant'] = tenant;
  const res = await fetch(apiUrl(path), { headers });
  if (!res.ok)
    throw new ApiError(res.status, i18n.t('common:errors.status', { status: res.status }), path);
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = `platform-${kind}-${filter.from}-${filter.to}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

// ---------------------------------------------------------------------------
// כתיבות
// ---------------------------------------------------------------------------

export interface CreateBusinessInput {
  businessName: string;
  trade: string;
  locale: 'he' | 'en';
  ownerName: string;
  ownerEmail: string;
  ownerPhone?: string;
}

export interface AddUserInput {
  name: string;
  role: Role;
  email?: string;
  phone?: string;
}

const userInviteSchema = inviteSchema.extend({
  member: z.object({ id: z.string().uuid(), name: z.string(), email: z.string() }),
});

export type BusinessAction = 'suspend' | 'reactivate' | 'schedule-deletion' | 'cancel-deletion';
export type UserAction = 'deactivate' | 'reactivate' | 'reset-password' | 'reset-mfa';

/** אחרי כל כתיבה: רשימות, מגירה, משתמשים ויומן. */
function useInvalidateAdmin() {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: ROOT });
}

export function useCreateBusiness() {
  const invalidate = useInvalidateAdmin();
  return useMutation({
    mutationFn: (input: CreateBusinessInput) =>
      request('/platform-admin/businesses', {
        method: 'POST',
        body: input,
        schema: createdBusinessSchema,
        idempotencyKey: crypto.randomUUID(),
      }),
    onSuccess: invalidate,
  });
}

export function useBusinessAction() {
  const invalidate = useInvalidateAdmin();
  return useMutation({
    mutationFn: (v: { id: string; action: BusinessAction; confirmName?: string }) =>
      request(`/platform-admin/businesses/${encodeURIComponent(v.id)}/${v.action}`, {
        method: 'POST',
        body: v.action === 'schedule-deletion' ? { confirmName: v.confirmName } : {},
        schema: businessStatusSchema,
      }),
    onSuccess: invalidate,
  });
}

export function useAddUser() {
  const invalidate = useInvalidateAdmin();
  return useMutation({
    mutationFn: (v: { businessId: string; input: AddUserInput }) =>
      request(`/platform-admin/businesses/${encodeURIComponent(v.businessId)}/users`, {
        method: 'POST',
        body: v.input,
        schema: userInviteSchema,
        idempotencyKey: crypto.randomUUID(),
      }),
    onSuccess: invalidate,
  });
}

export function useUserAction() {
  const invalidate = useInvalidateAdmin();
  return useMutation({
    mutationFn: (v: { businessId: string; userId: string; action: UserAction }) =>
      request(
        `/platform-admin/businesses/${encodeURIComponent(v.businessId)}/users/${encodeURIComponent(v.userId)}/${v.action}`,
        { method: 'POST', body: {} },
      ),
    onSuccess: invalidate,
  });
}

export const resetPasswordResultSchema = userInviteSchema;
